import { useMemo, useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, FileSpreadsheet, Lock } from 'lucide-react';
import {
  MAX_IMPORT_FILE_BYTES,
  MAX_IMPORT_ROWS,
  readSpreadsheet,
  suggestMapping,
  mappingIsComplete,
  type CustomerImportRequest,
  type CustomerImportResult,
  type ImportTarget,
  type MeasurementColumnCandidate,
  type SpreadsheetTable,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { ColumnMapper } from '../components/import/column-mapper.js';
import { ImportPreview } from '../components/import/import-preview.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Card, CardContent, CardHeader, CardTitle, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useImportCustomers, usePreviewImport, rejectedRowsCsv } from '../lib/import/use-import.js';
import { readFileBytes } from '../lib/import/read-file.js';
import { useTemplates } from '../lib/measurements/use-templates.js';

/**
 * Importing the customer list a shop already has, in four steps that can each be gone back on.
 *
 * The file is read HERE, in the browser, by the shared spreadsheet reader — CSV or Excel, the
 * bytes decide. Nothing is uploaded: what goes to the API is a grid of strings and a mapping,
 * so the server never handles a binary file and the same rows can be previewed as many times
 * as the owner needs before anything is written.
 *
 * The order of the steps is the order of trust. Choose a file, see what we think its columns
 * mean, see exactly what will happen to every row, and only then press the button that writes.
 * The rows that will fail are shown before the import rather than reported after it, because
 * afterwards the only remedy left is undoing something.
 */

type Stage = 'choose' | 'map' | 'preview' | 'done';

export function ImportCustomersPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const canImport = role === 'owner' || role === 'manager';

  const fileInput = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>('choose');
  const [fileName, setFileName] = useState('');
  const [table, setTable] = useState<SpreadsheetTable | null>(null);
  const [mapping, setMapping] = useState<ImportTarget[]>([]);
  const [garmentTypeKey, setGarmentTypeKey] = useState<string>('');
  const [readError, setReadError] = useState<string | null>(null);
  const [result, setResult] = useState<CustomerImportResult | null>(null);

  const templates = useTemplates();
  const preview = usePreviewImport();
  const run = useImportCustomers();

  /** The measurement fields the chosen garment offers — the extra column destinations. */
  const measurementFields: MeasurementColumnCandidate[] = useMemo(() => {
    const template = templates.data?.templates.find((one) => one.garmentTypeKey === garmentTypeKey);
    return (template?.fields ?? [])
      .filter((field) => field.active)
      .map((field) => ({ fieldKey: field.key, label: field.label }));
  }, [templates.data, garmentTypeKey]);

  if (!canImport) {
    return (
      <>
        <PageHeader title={t('import.title')} />
        <EmptyState
          icon={Lock}
          title={t('import.notAllowedTitle')}
          description={t('import.notAllowedHint')}
        />
      </>
    );
  }

  async function onFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    setReadError(null);
    setResult(null);

    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setReadError(t('import.tooLarge'));
      return;
    }

    try {
      const parsed = readSpreadsheet(await readFileBytes(file));
      if (parsed.header.length === 0 || parsed.rows.length === 0) {
        setReadError(t('import.empty'));
        return;
      }
      setFileName(file.name);
      setTable(parsed);
      // The guess runs against whatever garment is chosen at this point — none, usually. The
      // owner can choose one on the next screen and the measurement columns are re-guessed.
      setMapping(suggestMapping(parsed.header, measurementFields));
      setStage('map');
    } catch (error) {
      setReadError(error instanceof Error ? error.message : t('import.unreadable'));
    }
  }

  function chooseGarment(key: string): void {
    setGarmentTypeKey(key);
    if (!table) return;
    const template = templates.data?.templates.find((one) => one.garmentTypeKey === key);
    const fields = (template?.fields ?? [])
      .filter((field) => field.active)
      .map((field) => ({ fieldKey: field.key, label: field.label }));
    // Re-guess with the new garment's fields in play, so "Bust" stops being an ignored column
    // the moment the owner says which garment it belongs to.
    setMapping(suggestMapping(table.header, fields));
  }

  function requestFor(rows: SpreadsheetTable): CustomerImportRequest {
    return {
      mapping,
      rows: rows.rows.map((row) => ({ rowNumber: row.rowNumber, cells: [...row.cells] })),
      ...(garmentTypeKey ? { measurementGarmentTypeKey: garmentTypeKey } : {}),
    };
  }

  async function check(): Promise<void> {
    if (!table) return;
    try {
      setResult(await preview.mutateAsync(requestFor(table)));
      setStage('preview');
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  }

  async function commit(): Promise<void> {
    if (!table) return;
    try {
      const imported = await run.mutateAsync(requestFor(table));
      setResult(imported);
      setStage('done');
      toast({ title: t('import.done', { count: imported.summary.created }) });
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  }

  /**
   * The rejected rows, handed back as the file they came from with a column saying what was
   * wrong. A list of failures the owner has to read off a screen and retype is not a remedy.
   */
  function downloadRejected(): void {
    if (!table || !result) return;
    const csv = rejectedRowsCsv(table.header, result, t('import.rejectedReasonColumn'));
    if (!csv) return;

    // The byte-order mark is what makes Excel open a UTF-8 CSV without mangling every
    // name in it.
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = t('import.rejectedFileName', { name: fileName.replace(/\.[^.]+$/, '') });
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const rejectedCount = (result?.summary.failed ?? 0) + (result?.summary.skipped ?? 0);

  return (
    <>
      <PageHeader
        title={t('import.title')}
        description={t('import.description')}
        action={
          <Button asChild variant="ghost">
            <Link to="/customers">{t('import.backToCustomers')}</Link>
          </Button>
        }
      />

      <div className="flex flex-col gap-6">
        {stage === 'choose' ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('import.chooseTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                {t('import.chooseHint', { rows: MAX_IMPORT_ROWS })}
              </p>
              <div>
                <label className="sr-only" htmlFor="import-file">
                  {t('import.fileLabel')}
                </label>
                <input
                  ref={fileInput}
                  id="import-file"
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => void onFile(event)}
                  className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-medium"
                />
              </div>
              {readError ? <p className="text-sm text-destructive">{readError}</p> : null}
            </CardContent>
          </Card>
        ) : null}

        {stage === 'map' && table ? (
          <Card>
            <CardHeader className="flex-row items-center gap-3">
              <FileSpreadsheet aria-hidden="true" className="size-5 text-muted-foreground" />
              <CardTitle>{t('import.mapping.title', { file: fileName })}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                {t('import.mapping.found', { count: table.rows.length })}
                {table.truncated > 0 ? ` ${t('import.truncated', { count: table.truncated })}` : ''}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="import-garment" className="text-sm text-muted-foreground">
                  {t('import.mapping.garmentLabel')}
                </label>
                <select
                  id="import-garment"
                  value={garmentTypeKey}
                  onChange={(event) => chooseGarment(event.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="">{t('import.mapping.noGarment')}</option>
                  {(templates.data?.templates ?? []).map((template) => (
                    <option key={template.garmentTypeKey} value={template.garmentTypeKey}>
                      {template.garmentTypeLabel}
                    </option>
                  ))}
                </select>
              </div>

              <ColumnMapper
                header={table.header}
                sample={table.rows[0]?.cells ?? []}
                mapping={mapping}
                measurementFields={measurementFields}
                onChange={setMapping}
              />

              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={!mappingIsComplete(mapping) || preview.isPending}
                  onClick={() => void check()}
                >
                  {t('import.check')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStage('choose');
                    setTable(null);
                    if (fileInput.current) fileInput.current.value = '';
                  }}
                >
                  {t('import.chooseAnother')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {stage === 'preview' && result ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('import.previewTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">{t('import.previewHint')}</p>
              <ImportPreview result={result} />
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={result.summary.ready === 0 || run.isPending}
                  onClick={() => void commit()}
                >
                  {t('import.commit', { count: result.summary.ready })}
                </Button>
                <Button variant="ghost" onClick={() => setStage('map')}>
                  {t('import.backToMapping')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {stage === 'done' && result ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('import.doneTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <ImportPreview result={result} />
              <div className="flex flex-wrap gap-2">
                <Button asChild>
                  <Link to="/customers">{t('import.viewCustomers')}</Link>
                </Button>
                {rejectedCount > 0 ? (
                  <Button variant="outline" onClick={downloadRejected}>
                    <Download aria-hidden="true" className="mr-2 size-4" />
                    {t('import.downloadRejected', { count: rejectedCount })}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  );
}

export default ImportCustomersPage;
